import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, Type, ModuleWithProviders, APP_INITIALIZER } from '@angular/core';
import { TranslateModule, TranslateLoader, TranslateCompiler, TranslateParser, MissingTranslationHandler } from '@ngx-translate/core';
import { NgxsModule } from '@ngxs/store';
import 'moment/locale/be';
import 'moment/locale/en-gb';
import 'moment/locale/de';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/nl';
import 'moment/locale/da';
import 'moment/locale/sv';
// Each locale import becomes moment's global default; es stays last so that default is unchanged.
import 'moment/locale/es';
import { MESSAGE_FORMAT_CONFIG } from 'ngx-translate-messageformat-compiler';
import { Language } from './models/enums/language.enum';
import { ILocalizationOptions, LOCALIZATION_OPTIONS } from './models/i-localization-options';
import { IcuTranslatePipe } from './models/pipes/icu-translation.pipe';
import { TranslateServiceRegistry } from './models/translate-service-registry';
import { ILocalizationService } from './services/i-localization.service';
import { LocalizationOptionService } from './services/localization-option.service';
import { MissingTranslationHandlerService } from './services/missing-translation-handler.service';
import { TranslateICUCompiler } from './services/translate-icu-compiler.service';
import { httpLoaderFactory } from './services/translate-loader.factory';
import { TranslateParserService } from './services/translate-parser.service';
import { translateFactoryProvider } from './services/translate-service-factory';
import { LocalizationInitializerService } from './services/localization-initializer.service';
import { LocalizationState } from './store/localization.state';
import { localizationInitializerFactory } from './services/localization-initializer.factory';

@NgModule({
  declarations: [IcuTranslatePipe],
  exports: [IcuTranslatePipe],
})
export class LocalizationUIModule {}

@NgModule({
  declarations: [],
  exports: [TranslateModule, LocalizationUIModule],
  imports: [
    LocalizationUIModule,
    TranslateModule.forRoot({
      parser: {
        provide: TranslateParser,
        useClass: TranslateParserService,
      },
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient, LocalizationOptionService],
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateICUCompiler,
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MissingTranslationHandlerService,
      },
      defaultLanguage: Language.English,
      useDefaultLang: false,
    }),
    NgxsModule.forFeature([LocalizationState]),
  ],
  providers: [provideHttpClient(withInterceptorsFromDi())],
})
export class LocalizationRootModule {}

@NgModule({
  declarations: [],
  imports: [
    LocalizationUIModule,
    TranslateModule.forChild({
      parser: {
        provide: TranslateParser,
        useClass: TranslateParserService,
      },
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient, LocalizationOptionService],
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateICUCompiler,
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MissingTranslationHandlerService,
      },
      defaultLanguage: Language.English,
      useDefaultLang: false,
      isolate: true,
    }),
  ],
  exports: [TranslateModule, LocalizationUIModule],
})
export class LocalizationFeatureModule {}

@NgModule({
  declarations: [],
  imports: [
    TranslateModule.forChild({
      parser: {
        provide: TranslateParser,
        useClass: TranslateParserService,
      },
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient, LocalizationOptionService],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MissingTranslationHandlerService,
      },
      defaultLanguage: Language.English,
      useDefaultLang: false,
    }),
    LocalizationUIModule,
  ],
  exports: [TranslateModule, LocalizationUIModule],
})
export class LocalizationModule {
  /**
   * Call the forRoot only in the AppModule to set the translation loader
   */
  public static forRoot(
    localizationService: Type<ILocalizationService>,
    localizationOptions: ILocalizationOptions,
  ): ModuleWithProviders<LocalizationRootModule> {
    return {
      ngModule: LocalizationRootModule,
      providers: [
        TranslateServiceRegistry,
        { provide: ILocalizationService, useClass: localizationService },
        { provide: LOCALIZATION_OPTIONS, useValue: localizationOptions },
        {
          provide: MESSAGE_FORMAT_CONFIG,
          useValue: {
            locales: [
              Language.English,
              Language.French,
              Language.German,
              Language.Dutch,
              Language.Italian,
              Language.Danish,
              Language.Spanish,
              Language.Swedish,
            ],
          },
        },
        translateFactoryProvider,
        LocalizationOptionService,
        LocalizationInitializerService,
        {
          provide: APP_INITIALIZER,
          useFactory: localizationInitializerFactory,
          deps: [LocalizationInitializerService],
          multi: true,
        },
      ],
    };
  }
  /**
   * Call the forChild only if you want use different source for translation in the lazy loaded module else import the module without any call
   */
  public static forChild(localizationOptions: ILocalizationOptions): ModuleWithProviders<LocalizationFeatureModule> {
    return {
      ngModule: LocalizationFeatureModule,
      providers: [
        { provide: LOCALIZATION_OPTIONS, useValue: localizationOptions },
        {
          provide: MESSAGE_FORMAT_CONFIG,
          useValue: {
            locales: [
              Language.English,
              Language.French,
              Language.German,
              Language.Dutch,
              Language.Italian,
              Language.Danish,
              Language.Spanish,
              Language.Swedish,
            ],
          },
        },
        translateFactoryProvider,
        LocalizationOptionService,
      ],
    };
  }
}
